/* ============================================================
   阶段 E · cart 端 DIY 缩略图渲染(2026-06-02)
   --------------------------------------------------------------
   职责:扫 cart line items,对 DIY product item 用 line item properties
        里的 slug 信息,在客户端 canvas 上叠加 4 层 PNG,替换原 img,
        让 cart 显示客户具体 DIY 配色,而不是通用产品图。

   依赖:
   - window.RAINBOW_ASSETS(117 张零件 PNG 的 asset URL map,由 rainbow-asset-map snippet 注入)
   - properties 里的 _host_slug / _faceplate_slug{X} / _earpad_slug{X} / _headband_slug{X} / _moni_slug
   - properties 里的 _kit_count(1/2/3)
   ============================================================ */
(function () {
  'use strict';

  // 戴法数(兼容旧逻辑,作为 fallback)
  const LOOKS_PER_KIT = { 1: 4, 2: 15, 3: 38 };

  // 修复-17 · Shopify 后台需求(2026-06-02):按 variant_id 映射 Bundle/Duo/Trio + Looks
  const DIY_VARIANT_LABELS = {
    '46073729384621': { name: 'Bundle', looks: 4  },  // 1 副
    '46073729712301': { name: 'Duo',    looks: 15 },  // 2 副
    '46073731547309': { name: 'Trio',   looks: 36 },  // 3 副(11 件估算)
  };

  // 修复-17 · DIY product handle 白名单(防 cart 主页 Special Design Set 误渲染)
  const DIY_HANDLES = new Set(['rainbow-bundle', 'rainbow-duo', 'rainbow-trio']);

  // 适配图查找(对齐主 DIY UI rainbow-diy.js line 163-178 的 adaptImg):
  // 全包耳形态(默认)= 素材/适配图/${type}-${slug}.png(无形态后缀)
  // 半包耳形态(暂不用)= 素材/适配图/${type}-halfear-${slug}.png
  function adaptImg(type, slug) {
    if (!slug) return null;
    if (!window.RAINBOW_ASSETS) return null;
    return window.RAINBOW_ASSETS[`素材/适配图/${type}-${slug}.png`] || null;
  }

  // 加载单个图(返回 Promise<Image>)
  function loadImage(src) {
    return new Promise((resolve) => {
      if (!src) return resolve(null);
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => { console.warn('[Rainbow Cart Thumb] 加载失败:', src); resolve(null); };
      img.src = src;
    });
  }

  // 渲染 1 副耳机(5 层 PNG 叠加)到 canvas
  // 修复-4 · 跟 DIY UI build-card__thumb 完全对齐:shadow → earpad → faceplate → host → headband(5 层)
  async function renderOnePair(canvas, slugs) {
    const ctx = canvas.getContext('2d');
    const { host, faceplate, earpad, headband } = slugs;
    const sources = [
      window.RAINBOW_ASSETS['素材/适配图/shadow.png'] || null,   // ★ 修复-4 新增 shadow 投影层
      adaptImg('earpad', earpad),
      adaptImg('faceplate', faceplate),
      adaptImg('host', host),
      adaptImg('headband', headband),
    ];
    const images = await Promise.all(sources.map(loadImage));
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    for (const img of images) {
      if (img) ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    }
  }

  // E v2 方案 · 独立方块 + 横线分隔(2026-06-16 第二轮):
  // - 每副耳机 / 魔尼带各自独立成"小卡片"(浅灰底 + 圆角 + 轻阴影),不再贴成一条
  // - 卡片之间用横线分隔(明显视觉边界,跟主题原生 cart 风格协调)
  // - 仍然在 line item 左侧 80 宽窄列,右侧 properties 文字完全不动
  async function renderItem(itemEl, itemData) {
    const img = itemEl.querySelector('img');
    const props = itemData.properties || {};
    const kitCount = parseInt(props._kit_count || '1', 10) || 1;
    const moniSlug = props._moni_slug || null;

    // 核心常量
    const COL_W = 86;       // 左列总宽(给 tile 留 padding)
    const TILE_PAD = 5;     // tile 内边距(让 canvas 在卡片里有呼吸)
    const TILE = 70;        // canvas / img 实际尺寸
    const CAPTION_H = 11;
    const DPR = window.devicePixelRatio || 1;

    // 主容器:垂直 flex
    // 2026-06-16 第三轮修复:width 改成 100% + max-width 86,跟随主题容器自适应,
    // 不再强行撑到 86 宽,避免主题 cart-item__image-container 被推宽挤出右侧文字
    const container = document.createElement('div');
    container.className = 'rainbow-cart-thumb rainbow-cart-thumb--blocks';
    container.style.cssText = `
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0;
      width: 100%;
      max-width: ${COL_W}px;
      padding: 4px 0;
      box-sizing: border-box;
      background: transparent;
      vertical-align: middle;
    `;

    // 2026-06-17 第四轮修复:用户反馈"上下没分隔,堵在一块"。
    // 根因:之前 divider 用 div + background 太细 (#e0e0e0 1px) 易被主题 CSS 盖过去。
    // 这次:① divider 改用 border-top(border 比 background 难 override) ② 更粗 2px + 更深 #c0c0c0
    //       ③ tile 自带 margin 4px,即便 divider 全没了 tile 之间也有空隙
    //       ④ tile 加 box-shadow 更立体浮起(visual"卡片"感强)

    const makeTile = () => {
      const t = document.createElement('div');
      t.style.cssText = `
        background: #f5f5f5;
        border-radius: 10px;
        padding: ${TILE_PAD}px;
        box-shadow: 0 2px 6px rgba(0, 0, 0, .10);
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 2px;
        box-sizing: content-box;
        margin: 4px 0;
      `;
      return t;
    };

    const makeDivider = () => {
      const hr = document.createElement('div');
      hr.style.cssText = `
        width: 80%;
        border-top: 2px solid #c0c0c0;
        margin: 6px 0;
        height: 0;
        box-sizing: border-box;
      `;
      return hr;
    };

    let isFirst = true;

    // ── N 副耳机(每副独立方块) ──
    for (let i = 1; i <= kitCount; i++) {
      if (!isFirst) container.appendChild(makeDivider());
      isFirst = false;

      const suffix = kitCount > 1 ? ` (Set ${i})` : '';
      const slugs = {
        host:      props._host_slug || props[`_host_slug${suffix}`] || null,
        faceplate: props[`_faceplate_slug${suffix}`] || props._faceplate_slug || null,
        earpad:    props[`_earpad_slug${suffix}`]    || props._earpad_slug    || null,
        headband:  props[`_headband_slug${suffix}`]  || props._headband_slug  || null,
      };
      const tile = makeTile();

      const canvas = document.createElement('canvas');
      canvas.width = TILE * DPR;
      canvas.height = TILE * DPR;
      canvas.style.cssText = `
        width: ${TILE}px;
        height: ${TILE}px;
        display: block;
      `;
      canvas.setAttribute('aria-label', `DIY 第 ${i} 副耳机配色`);
      tile.appendChild(canvas);

      if (kitCount > 1) {
        const cap = document.createElement('div');
        cap.textContent = `Set ${i}`;
        cap.style.cssText = `
          font-size: 9px;
          line-height: ${CAPTION_H}px;
          color: #6B4F2C;
          font-weight: 600;
          letter-spacing: .02em;
        `;
        tile.appendChild(cap);
      }
      container.appendChild(tile);
      await renderOnePair(canvas, slugs);
    }

    // ── 魔尼带方块(最底) ──
    if (moniSlug && window.RAINBOW_ASSETS) {
      const moniUrl = window.RAINBOW_ASSETS[`moni-strap-${moniSlug}.png`]
                   || window.RAINBOW_ASSETS[`素材/moni-strap-${moniSlug}.png`]
                   || null;
      if (moniUrl) {
        if (!isFirst) container.appendChild(makeDivider());
        isFirst = false;

        const tile = makeTile();
        const moniWrap = document.createElement('div');
        moniWrap.style.cssText = `
          width: ${TILE}px;
          height: ${TILE}px;
          display: flex;
          align-items: center;
          justify-content: center;
        `;
        const moniImg = document.createElement('img');
        moniImg.src = moniUrl;
        moniImg.alt = `Moni-Strap ${moniSlug}`;
        moniImg.style.cssText = `
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
          pointer-events: none;
        `;
        moniWrap.appendChild(moniImg);
        tile.appendChild(moniWrap);

        const cap = document.createElement('div');
        cap.textContent = 'Moni-Strap';
        cap.style.cssText = `
          font-size: 9px;
          line-height: ${CAPTION_H}px;
          color: #6B4F2C;
          font-weight: 600;
          letter-spacing: .02em;
        `;
        tile.appendChild(cap);
        container.appendChild(tile);
      }
    }

    // 兼容 img 存在 / 不存在 — 直接替换或塞入 image slot
    let parentSlot;
    if (img) {
      parentSlot = img.parentElement;
      img.replaceWith(container);
    } else {
      const slot = itemEl.querySelector(
        '[class*="image"], [class*="thumbnail"], [class*="figure"], [class*="media"], [class*="photo"]'
      );
      if (slot) { slot.innerHTML = ''; slot.appendChild(container); parentSlot = slot; }
      else { itemEl.prepend(container); parentSlot = container.parentElement; }
    }

    // 2026-06-16 第三轮修复:不再强制 parentSlot 锁宽,只设 maxWidth + flexShrink:0
    // 防止"右侧文字溢出屏幕"——主题 cart-item__image-container 维持自己的尺寸,我们只限上限
    if (parentSlot) {
      parentSlot.style.maxWidth = `${COL_W}px`;
      parentSlot.style.flexShrink = '0';
    }

    // Bundle / Duo / Trio · X Looks 角标(挂在 container 下方,跟随窄列宽)
    const variantLabel = DIY_VARIANT_LABELS[String(itemData.variant_id)];
    const looks = variantLabel ? variantLabel.looks : LOOKS_PER_KIT[kitCount];
    if (looks) {
      const badge = document.createElement('div');
      badge.className = 'rainbow-cart-thumb__looks';
      const ordinals = { 1: '1st', 2: '2nd', 3: '3rd' };
      badge.textContent = variantLabel
        ? `${variantLabel.name} · ${variantLabel.looks} Looks`
        : `${ordinals[kitCount]} pair · ${looks} looks`;
      badge.style.cssText = `
        font-size: 9px;
        color: #6B4F2C;
        margin-top: 4px;
        margin-bottom: 6px;
        font-weight: 700;
        letter-spacing: .02em;
        text-align: center;
        width: ${COL_W}px;
        white-space: nowrap;
      `;
      container.parentElement?.appendChild(badge);
    }

    itemEl.dataset.rainbowDiyRendered = '1';
  }

  // 找 cart line item 的 DOM 容器(尝试多种 selector,主题差异容错)
  function findItemElement(item) {
    const k = item.key;
    const vid = item.variant_id;
    const selectors = [
      `[data-line-key="${k}"]`,
      `[data-key="${k}"]`,
      `[data-line-item-key="${k}"]`,
      `[data-item-key="${k}"]`,
      `[id="CartItem-${k}"]`,
      `[data-variant-id="${vid}"]`,
      `[data-variant="${vid}"]`,
    ];
    for (const sel of selectors) {
      const el = document.querySelector(sel);
      if (el) return el;
    }
    // 兜底:用 properties 文本扫(找含 "Host Color" 且未渲染过的)
    const candidates = document.querySelectorAll('[class*="cart-item"], li.cart-item, tr.cart-item, .cart__items > *, [class*="line-item"]');
    for (const el of candidates) {
      if (el.dataset.rainbowDiyRendered) continue;
      if (el.textContent && el.textContent.includes('Host Color')) {
        return el;
      }
    }
    return null;
  }

  async function scanAndRender() {
    try {
      const resp = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!resp.ok) return;
      const cart = await resp.json();
      for (const item of cart.items || []) {
        if (!item.properties || !item.properties['Host Color']) continue;
        // 修复-17 · handle 白名单防误渲染(主页 Special Design Set 等其他 cart line item)
        // 如果 cart.js 给了 handle 且不在白名单 → 跳过
        if (item.handle && !DIY_HANDLES.has(item.handle)) continue;
        const itemEl = findItemElement(item);
        if (!itemEl || itemEl.dataset.rainbowDiyRendered === '1') continue;
        await renderItem(itemEl, item);
      }
    } catch (e) {
      console.warn('[Rainbow Cart Thumb] scan 失败:', e);
    }
  }

  // 入口:DOM ready 跑一次 + MutationObserver 监听 cart drawer 异步加载
  function init() {
    scanAndRender();
    // cart drawer / ajax cart 更新后 DOM 变化,debounce 重扫
    let timer;
    new MutationObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(scanAndRender, 250);
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
